import { SortDirection } from "./TableColumnHeader.tsx";
import { NumberRange } from "./numberRange.ts";
import { DateRange } from "./dateRange.ts";

export function setOrDeleteParam(
  params: URLSearchParams,
  name: string,
  value: string | undefined,
): void {
  if (value === undefined || value === "") params.delete(name);
  else params.set(name, value);
}

export function parseListParam(value: string | null): string[] {
  return value?.split(",").filter(Boolean) ?? [];
}

export function parseKnownListParam<Value extends string>(
  value: string | null,
  knownValues: readonly Value[],
): Value[] {
  return parseListParam(value).filter((item): item is Value =>
    (knownValues as readonly string[]).includes(item),
  );
}

export type TableSort<Field extends string> = {
  sortBy: Field;
  sortDirection: SortDirection;
};

export function parseSortParam<Field extends string>(
  value: string | null,
  knownFields: readonly Field[],
): TableSort<Field> | undefined {
  const [sortBy, sortDirection] = value?.split(":") ?? [];
  const isKnownField = (knownFields as readonly string[]).includes(sortBy);
  const isKnownDirection = sortDirection === "asc" || sortDirection === "desc";
  return isKnownField && isKnownDirection
    ? { sortBy: sortBy as Field, sortDirection }
    : undefined;
}

export function formatSortParam<Field extends string>(
  sort: TableSort<Field>,
): string {
  return `${sort.sortBy}:${sort.sortDirection}`;
}

function parseNumberParam(value: string | null): number | undefined {
  if (!value) return undefined;
  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
}

export function parseRangeParams(
  params: URLSearchParams,
  name: string,
): NumberRange {
  return {
    min: parseNumberParam(params.get(`${name}Min`)),
    max: parseNumberParam(params.get(`${name}Max`)),
  };
}

export function setRangeParams(
  params: URLSearchParams,
  name: string,
  range: NumberRange,
): void {
  setOrDeleteParam(params, `${name}Min`, range.min?.toString());
  setOrDeleteParam(params, `${name}Max`, range.max?.toString());
}

export function parseDateRangeParams(params: URLSearchParams): DateRange {
  return {
    from: params.get("from") ?? undefined,
    to: params.get("to") ?? undefined,
  };
}

export function setDateRangeParams(
  params: URLSearchParams,
  range: DateRange,
): void {
  setOrDeleteParam(params, "from", range.from);
  setOrDeleteParam(params, "to", range.to);
}
